import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Sistema de idiomas do ClickHub - pedido pelo dono do sistema: "todo
 * sistema tem que ter o seletor de idioma", funcionando tanto para
 * quem está no Brasil quanto fora, brasileiro ou não.
 *
 * Como funciona: cada tela usa o hook useIdioma() para pegar a função
 * t("chave") que devolve o texto traduzido. Se uma chave não tem
 * tradução ainda, mostra a própria chave (nunca quebra a tela).
 *
 * IDIOMAS_DISPONIVEIS é a lista de bandeiras que aparecem no seletor -
 * pedido para cobrir os principais mercados: Brasil, EUA, e o núcleo da
 * Europa que já confirmamos ter TikTok Shop ativo (UK, Alemanha,
 * França, Espanha) mais Canadá.
 */

export const IDIOMAS_DISPONIVEIS = [
  { codigo: "pt-BR", nome: "Português (Brasil)", bandeira: "🇧🇷" },
  { codigo: "en-US", nome: "English (US)", bandeira: "🇺🇸" },
  { codigo: "en-CA", nome: "English (Canada)", bandeira: "🇨🇦" },
  { codigo: "en-GB", nome: "English (UK)", bandeira: "🇬🇧" },
  { codigo: "de-DE", nome: "Deutsch", bandeira: "🇩🇪" },
  { codigo: "fr-FR", nome: "Français", bandeira: "🇫🇷" },
  { codigo: "es-ES", nome: "Español", bandeira: "🇪🇸" },
  { codigo: "it-IT", nome: "Italiano", bandeira: "🇮🇹" },
  { codigo: "ar-AE", nome: "العربية (الإمارات)", bandeira: "🇦🇪", rtl: true },
  { codigo: "zh-CN", nome: "中文 (简体)", bandeira: "🇨🇳" },
];

const CHAVE_ARMAZENAMENTO = "clickhub_idioma";

const IdiomaContext = createContext(null);

export const IdiomaProvider = ({ children }) => {
  const [idioma, setIdiomaState] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_ARMAZENAMENTO) || "pt-BR";
    } catch {
      return "pt-BR";
    }
  });
  const [traducoes, setTraducoes] = useState({});

  useEffect(() => {
    let cancelado = false;
    import(`./traducoes/${idioma}.json`)
      .then((modulo) => {
        if (!cancelado) setTraducoes(modulo.default || modulo);
      })
      .catch(() => {
        // Idioma sem arquivo de tradução ainda (em construção) - usa
        // as chaves originais em português como fallback, nunca quebra.
        if (!cancelado) setTraducoes({});
      });
    return () => {
      cancelado = true;
    };
  }, [idioma]);

  const setIdioma = (novoIdioma) => {
    setIdiomaState(novoIdioma);
    try {
      localStorage.setItem(CHAVE_ARMAZENAMENTO, novoIdioma);
    } catch {
      // localStorage indisponível - segue sem persistir, não quebra
    }
  };

  useEffect(() => {
    // Árabe se lê da direita para a esquerda - aplica isso na tela
    // inteira automaticamente, senão o layout fica visualmente errado
    // (textos, ícones e alinhamentos no lado errado).
    const idiomaAtual = IDIOMAS_DISPONIVEIS.find((i) => i.codigo === idioma);
    document.documentElement.dir = idiomaAtual?.rtl ? "rtl" : "ltr";
    document.documentElement.lang = idioma;
  }, [idioma]);

  const t = (chave, textoOriginal) => {
    return traducoes[chave] || textoOriginal || chave;
  };

  return (
    <IdiomaContext.Provider value={{ idioma, setIdioma, t, idiomas: IDIOMAS_DISPONIVEIS }}>
      {children}
    </IdiomaContext.Provider>
  );
};

export const useIdioma = () => {
  const contexto = useContext(IdiomaContext);
  if (!contexto) {
    throw new Error("useIdioma precisa ser usado dentro de um IdiomaProvider");
  }
  return contexto;
};
